// GoodEnglishPage.js
// Good English 单页组件：图片居中 + 台词 + 播放按钮
import React, { useRef, useEffect, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PauseIcon from '@mui/icons-material/Pause';

/**
 * GoodEnglishPage
 *
 * props:
 *   page: { image, images, text, start, end }  — 页面数据
 *     - image: 单张图片（字符串）
 *     - images: 多张图片数组（垂直排列）
 *     - text: 台词
 *     - start/end: 音频起止秒数
 *   audioSrc: string                   — 共享音频源
 *   onAudioRef: (ref) => void         — 共享 audio 元素给父组件
 */
function GoodEnglishPage({ page, audioSrc, onAudioRef }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  // 暴露 audio ref 给父组件
  useEffect(() => {
    if (onAudioRef && audioRef.current) {
      onAudioRef(audioRef.current);
    }
  }, [onAudioRef]);

  const handlePlay = () => {
    if (!audioRef.current) return;

    const audio = audioRef.current;

    if (playing) {
      // 暂停
      audio.pause();
      setPlaying(false);
    } else {
      // 播放指定区间
      audio.currentTime = page.start;
      audio.play();

      // 设置结束时间
      if (page.end) {
        const checkEnd = () => {
          if (audio.currentTime >= page.end) {
            audio.pause();
            setPlaying(false);
            audio.removeEventListener('timeupdate', checkEnd);
          }
        };
        audio.addEventListener('timeupdate', checkEnd);
      }
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        p: 1,
      }}
    >
      {/* 故事图片（支持单图或多图垂直排列） */}
      {page.images ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {page.images.map((img, idx) => (
            <Box
              key={idx}
              component="img"
              src={img}
              alt={`${page.text} ${idx + 1}`}
              sx={{
                maxWidth: '85%',
                maxHeight: '28vh',
                objectFit: 'contain',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              }}
            />
          ))}
        </Box>
      ) : (
        <Box
          component="img"
          src={page.image}
          alt={page.text}
          sx={{
            maxWidth: '85%',
            maxHeight: '60vh',
            objectFit: 'contain',
            borderRadius: 2,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            flexShrink: 0,
          }}
        />
      )}

      {/* 台词 + 播放按钮 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 0.5,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: '1.4rem',
            fontWeight: 600,
            color: '#333',
            lineHeight: 1.3,
          }}
        >
          {page.text}
        </span>

        <Tooltip title={playing ? '暂停' : '播放'}>
          <IconButton
            onClick={handlePlay}
            className={playing ? 'playing' : ''}
            sx={{
              backgroundColor: playing ? '#FF6B35' : '#FF9F1C',
              color: 'white',
              width: 48,
              height: 48,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: playing ? '#E55A2B' : '#FF8C00',
              },
              '&.playing': {
                backgroundColor: '#FF6B35',
              },
            }}
          >
            {playing ? <PauseIcon sx={{ fontSize: 28 }} /> : <VolumeUpIcon sx={{ fontSize: 28 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* 隐藏的音频元素 */}
      <audio ref={audioRef} src={audioSrc} preload="auto" />
    </Box>
  );
}

export default GoodEnglishPage;
